import { computed, defineComponent, onMounted, onUnmounted, ref, watch, } from 'vue';
import { Button, WidgetInput } from '@yellowcard/components-library-vue3';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import TransactHeader from '@/components/transact/TransactHeader.vue';
import { numberFormat, isValidEmail } from '@/utils/helpers';
import useTransactionsStore from '@/store/modules/transactions';
import { useAuthStore } from '@/store/modules/auth';
import { storeToRefs } from 'pinia';
import { AuthTypes } from '@/types/auth';
import { TransactTypes } from '@/types/transact';
import router from '@/router';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import { loadTurnstileScript, onTurnstileInteractive, removeTurnstile, renderTurnstile, resetTurnstile, waitForTurnstileToken, } from '@/utils/turnstile';
import en from '@/locale/en.locale';
export default defineComponent({
    name: 'EmailInput',
    components: {
        Button,
        WidgetInput,
        LoaderComponent,
        TransactHeader,
    },
    setup() {
        const transactionsStore = useTransactionsStore();
        const { loadingMessage, currentStep: buyCurrentStep } = storeToRefs(transactionsStore);
        const authStore = useAuthStore();
        const { currentStep, email, standaloneAuthFlow, userNotRegistered, partnerId, } = storeToRefs(authStore);
        const error = ref(undefined);
        const rules = computed(() => ({
            email: (value) => isValidEmail(value) ? true : 'Please enter a valid email address',
        }));
        const buttonDisabled = ref(true);
        const loading = ref(false);
        const emailError = ref('');
        const turnstileContainer = ref(null);
        const challengeVisible = ref(false);
        const signUpMessage = () => challengeVisible.value
            ? 'Please complete the verification to continue'
            : 'Signing up';
        const mountTurnstile = async () => {
            if (!turnstileContainer.value)
                return;
            try {
                await renderTurnstile(turnstileContainer.value);
            }
            catch (e) {
                // Fail closed: signup then has no token and fails with the message the
                // backend would have returned anyway.
                console.error(`failed to render turnstile: ${e.message}`);
            }
        };
        // Only signup needs a token - `signIn` creates no Cognito user - so build the
        // widget when the flow turns into one. A user who only signs in is never
        // challenged, and the token cannot expire (~300s) on the email screen.
        watch(userNotRegistered, isSignup => {
            if (isSignup)
                mountTurnstile();
        });
        // Loading the script renders nothing, so it is safe on the sign-in path. It
        // keeps the download out of the short time the widget has to solve.
        onMounted(() => {
            loadTurnstileScript().catch((e) => {
                console.error(`failed to load turnstile: ${e.message}`);
            });
        });
        const stopListeningForChallenge = onTurnstileInteractive(visible => {
            challengeVisible.value = visible;
            if (loading.value)
                loadingMessage.value = signUpMessage();
        });
        // The container dies with this component, so the widget has to go with it.
        onUnmounted(() => {
            stopListeningForChallenge();
            removeTurnstile();
        });
        buttonDisabled.value = rules.value.email(email.value) !== true;
        watch(email, newInput => {
            buttonDisabled.value = rules.value.email(newInput) !== true;
            userNotRegistered.value = false;
        });
        const signIn = async () => {
            error.value = undefined;
            loading.value = true;
            loadingMessage.value = 'Sending you a secure OTP via email';
            buttonDisabled.value = true;
            try {
                await authStore.SignIn({
                    email: email.value,
                });
                loading.value = false;
                loadingMessage.value = '';
                currentStep.value = AuthTypes.AuthSteps.AUTH_2;
            }
            catch (e) {
                if (e.message.includes('User does not exist')) {
                    userNotRegistered.value = true;
                    loading.value = false;
                    loadingMessage.value = '';
                    buttonDisabled.value = false;
                }
                else if (e.message.includes('user is blocked')) {
                    loading.value = false;
                    loadingMessage.value = '';
                    currentStep.value = AuthTypes.AuthSteps.BLOCKED;
                }
                else {
                    error.value = e.message;
                    loading.value = false;
                    loadingMessage.value = '';
                }
            }
        };
        const signUp = async () => {
            try {
                error.value = undefined;
                loading.value = true;
                loadingMessage.value = signUpMessage();
                buttonDisabled.value = true;
                // No-op once the widget is up; covers a reload that skipped the watcher,
                // since pinia state comes back from sessionStorage already on this path.
                await mountTurnstile();
                const turnstileToken = await waitForTurnstileToken();
                loadingMessage.value = 'Signing up';
                await authStore.SignUp({
                    email: email.value,
                    turnstileToken,
                });
                await signIn();
            }
            catch (e) {
                // Turnstile tokens are single-use, so a retry needs a fresh one.
                resetTurnstile();
                error.value = e.message;
                loading.value = false;
                loadingMessage.value = '';
                // Re-enable the button, or one failure ends the signup.
                buttonDisabled.value = rules.value.email(email.value) !== true;
            }
        };
        const viewPrivacyPolicy = (evt) => {
            evt.stopImmediatePropagation();
            window.open(en.home.menu.privacyLink, '_blank');
        };
        const viewTermsOfUse = (evt) => {
            evt.stopImmediatePropagation();
            window.open(en.home.menu.tosLink, '_blank');
        };
        const clickContinue = async () => {
            trackEvent(MIXPANEL_EVENTS.SIGNIN.EMAIL, {
                partner: partnerId.value,
            });
            if (userNotRegistered.value) {
                await signUp();
            }
            else {
                await signIn();
            }
        };
        const goBack = () => {
            router.push('/transact');
            if (standaloneAuthFlow.value) {
                standaloneAuthFlow.value = false;
                return;
            }
            buyCurrentStep.value = TransactTypes.TransactSteps.BUY_3;
        };
        const onKeyPress = () => {
            console.log('');
        };
        return {
            emailError,
            numberFormat,
            email,
            buttonDisabled,
            clickContinue,
            signIn,
            signUp,
            rules,
            loading,
            error,
            userNotRegistered,
            goBack,
            standaloneAuthFlow,
            viewPrivacyPolicy,
            onKeyPress,
            viewTermsOfUse,
            turnstileContainer,
        };
    },
});
